import { globalStores } from '@/editor/stores';
import {
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  Shape,
  ShapeGeometry,
  Vector3
} from 'three';
import BaseModel2d from '../BaseModel2d';
import { pixelRatio2d } from '@/editor/math/const';
import { Line2, LineGeometry, LineMaterial } from 'three/examples/jsm/Addons.js';
import { ENTITY_LATER, ENTITY_PRIORITY, ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { IDimensionPoint } from '@/editor/data/dimension/dimension';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { Point2dUtils } from '@/editor/math/Point2dUtils';

const RECT_SIZE = 0.16;
const CAPSULE_HEIGHT = 0.06;
const CAPSULE_WIDTH = 0.28;
const LINE_WIDTH = 1;

const ACTIVE_COLOR = 0x1a7af8;

export default class AreaTransformHelper extends BaseModel2d {
  private _lineMaterial: LineMaterial;

  private _polygon: IDimensionPoint[] = [];

  constructor() {
    super();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.transformHelper);

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);

    this._lineMaterial = new LineMaterial({
      color: ACTIVE_COLOR,
      transparent: true,
      linewidth: LINE_WIDTH // 宽度（需启用渲染器支持）
    });
  }

  update(polygon: IDimensionPoint[]) {
    this._polygon = [...polygon];
    this.clear();
    this.createModel();
  }

  createModel() {
    const handlesMap = this._createControlHandler();
    for (const [key, object] of handlesMap.entries()) {
      object.userData.entityId = key.join('-');
      object.userData.type = EEntityType.AreaTransform;
      object.userData.priority = ENTITY_PRIORITY.transformHelper;
      object.userData.containerType = EContainerType.entity;
      this._modelGroup.add(object);
    }
  }

  private _createControlHandler() {
    const handlesMap: Map<string[], Object3D> = new Map();

    for (let i = 0; i < this._polygon.length; i++) {
      const current = this._polygon[i];
      const next = this._polygon[(i + 1) % this._polygon.length];
      const currentRect = this.createRect();
      currentRect.position.set(current.x / pixelRatio2d, current.y / pixelRatio2d, ENTITY_RENDER_ORDER.transformHelper);
      handlesMap.set([current.id], currentRect);

      const midRect = this._createMidCapsule(current, next);
      handlesMap.set([current.id, next.id], midRect);
    }

    return handlesMap;
  }

  private createRect() {
    const zoom = globalStores.scene2dZoom / 100;
    const width = RECT_SIZE / zoom;
    const height = RECT_SIZE / zoom;
    const group = new Group();

    const bgPlane = new PlaneGeometry(width, height);
    const bgMesh = new Mesh(
      bgPlane,
      new MeshBasicMaterial({
        color: 0x000000,
        opacity: 0,
        transparent: true
      })
    );
    group.add(bgMesh);

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([
      -width / 2,
      -height / 2,
      0,
      width / 2,
      -height / 2,
      0,
      width / 2,
      height / 2,
      0,
      -width / 2,
      height / 2,
      0,
      -width / 2,
      -height / 2,
      0
    ]);
    const rectMesh = new Line2(lineGeometry, this._lineMaterial);
    rectMesh.layers.set(ENTITY_LATER.TRANSFORM_LAYER);
    group.add(rectMesh);

    return group;
  }

  private _createMidCapsule(startPoint: IPoint2d, endPoint: IPoint2d) {
    const zoom = globalStores.scene2dZoom / 100;
    const capsuleHeight = CAPSULE_HEIGHT / zoom;
    const capsuleWidth = CAPSULE_WIDTH / zoom;
    const radius = capsuleHeight / 2;
    const halfStraight = capsuleWidth / 2 - radius;

    const group = new Group();
    group.renderOrder = ENTITY_RENDER_ORDER.transformHelper;

    const shape = new Shape();
    shape.moveTo(-halfStraight, -radius);
    shape.lineTo(halfStraight, -radius);
    shape.absarc(halfStraight, 0, radius, -Math.PI / 2, Math.PI / 2, false);
    shape.lineTo(-halfStraight, radius);
    shape.absarc(-halfStraight, 0, radius, Math.PI / 2, Math.PI * 1.5, false);

    const geometry = new ShapeGeometry(shape);
    const material = new MeshBasicMaterial({
      color: ACTIVE_COLOR,
      side: DoubleSide,
      transparent: true,
      opacity: 1
    });

    const capsule = new Mesh(geometry, material);

    const start = new Vector3(startPoint.x / pixelRatio2d, startPoint.y / pixelRatio2d, 0);
    const end = new Vector3(endPoint.x / pixelRatio2d, endPoint.y / pixelRatio2d, 0);
    const segmentDir = new Vector3().subVectors(end, start);

    const center = Point2dUtils.getCenter(startPoint, endPoint);
    capsule.position.set(center.x / pixelRatio2d, center.y / pixelRatio2d, 0);

    const quaternion = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), segmentDir.normalize());
    capsule.setRotationFromQuaternion(quaternion);

    group.add(capsule);
    return group;
  }

  override clear() {
    super.clear();
  }
}
